import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Sparkles, Check, RefreshCw, Apple, Lock, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { detectPlatform, isIOSNative, isWeb, stripeEnabled, appleIAPEnabled } from "../lib/platform";
import { fetchProductInfo, purchaseFullAccess, restorePurchases } from "../lib/iap";
import { stripeFetchPrice, stripeCreateCheckout, http } from "../lib/api";
import { useT } from "../lib/i18n";

const FEATURES_MS = [
  "Perbualan tanpa had dengan TAi-P",
  "Itinerari lanjutan (multi-destinasi, ride & offbeat)",
  "PDF majalah tanpa watermark",
  "Sejarah perjalanan tanpa had",
  "Muat naik PDF/imej tanpa had",
  "Muslim Friendly (masjid, halal, waktu solat)",
  "Sokongan keutamaan",
];
const FEATURES_EN = [
  "Unlimited chat with TAi-P",
  "Advanced itineraries (multi-destination, ride & offbeat)",
  "Magazine PDF without watermark",
  "Unlimited trip history",
  "Unlimited PDF/image uploads",
  "Muslim Friendly (mosques, halal, prayer times)",
  "Priority support",
];

function formatMoney(amount, currency) {
  if (amount == null || currency == null) return null;
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency: currency.toUpperCase() }).format(amount / 100);
  } catch { return `${amount / 100} ${currency.toUpperCase()}`; }
}

export default function Paywall() {
  const platform = detectPlatform();
  const { user, login } = useAuth();
  const { lang } = useT();
  const navigate = useNavigate();
  const FEATURES = lang === "en" ? FEATURES_EN : FEATURES_MS;
  const isIOS = platform === "ios_native" && appleIAPEnabled();
  const isWebFlow = platform === "web" && stripeEnabled();

  const [appleProduct, setAppleProduct] = useState(null);
  const [stripePrice, setStripePrice] = useState(null);
  const [currency, setCurrency] = useState(lang === "ms" ? "myr" : "usd");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // Auto-align default currency with UI language
    setCurrency((c) => c || (lang === "ms" ? "myr" : "usd"));
  }, [lang]);

  useEffect(() => {
    if (isIOS) {
      fetchProductInfo().then(setAppleProduct);
    } else if (isWebFlow) {
      stripeFetchPrice(currency).then((p) => p?.enabled && setStripePrice(p));
    }
  }, [isIOS, isWebFlow, currency]);

  const buyStripe = async () => {
    if (!user) { toast.info(lang === "en" ? "Please sign in first." : "Sila log masuk dahulu."); login(); return; }
    setBusy(true);
    try {
      const res = await stripeCreateCheckout(window.location.origin, currency);
      window.location.href = res.checkout_url;
    } catch (e) {
      toast.error(String(e.response?.data?.detail || e.message || "Payment error"));
      setBusy(false);
    }
  };

  const apiPost = async (path, body) => (await http.post(path, body)).data;

  const buyApple = async () => {
    if (!user) { toast.info(lang === "en" ? "Please sign in first." : "Sila log masuk dahulu."); login(); return; }
    setBusy(true);
    try {
      await purchaseFullAccess(apiPost);
      toast.success(lang === "en" ? "Full Access unlocked." : "Full Access telah diaktifkan.");
      navigate("/settings", { replace: true });
    } catch (e) {
      toast.error(String(e.message || (lang === "en" ? "Purchase failed" : "Pembelian gagal")));
    } finally { setBusy(false); }
  };

  const restoreApple = async () => {
    setBusy(true);
    try {
      await restorePurchases(apiPost);
      toast.success(lang === "en" ? "Purchase restored." : "Pembelian dipulihkan.");
      navigate("/settings", { replace: true });
    } catch (e) {
      toast.error(String(e.message || (lang === "en" ? "No prior purchase" : "Tiada pembelian")));
    } finally { setBusy(false); }
  };

  const priceLabel = isIOS
    ? (appleProduct?.price || (lang === "en" ? "—" : "—"))
    : (stripePrice ? formatMoney(stripePrice.unit_amount, stripePrice.currency) : (lang === "en" ? "Loading…" : "Sedang memuat…"));

  const titleMs = "Buka Akses Penuh GoTripStar AI Planner";
  const titleEn = "Unlock GoTripStar AI Planner Full Access";
  const descMs = "Bayar sekali sahaja. Tiada langganan dan tiada pembaharuan automatik.";
  const descEn = "One-time payment. No subscriptions and no auto-renewal.";

  return (
    <div className="min-h-screen bg-[#FBFBFD]">
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-white/70 border-b border-[#E8E8ED]">
        <div className="max-w-2xl mx-auto px-4 md:px-6 h-14 flex items-center gap-3">
          <Link to="/settings" className="p-2 -ml-2 rounded-full hover:bg-[#F5F5F7]" data-testid="paywall-back-btn">
            <ArrowLeft className="w-5 h-5 text-[#0A2540]" />
          </Link>
          <div className="font-semibold text-[#0A2540]">TAi-P Full Access</div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 md:px-6 py-6 space-y-6">
        {/* Hero */}
        <section className="bg-gradient-to-br from-[#0A2540] to-[#15374E] text-white rounded-[1.5rem] p-8 shadow-[0_12px_48px_rgba(10,37,64,0.25)] relative overflow-hidden">
          <div className="absolute -right-10 -top-10 w-56 h-56 rounded-full bg-[#D4AF37]/20 blur-3xl" />
          <div className="absolute -left-10 bottom-0 w-64 h-64 rounded-full bg-[#4FB3E8]/15 blur-3xl" />
          <div className="relative">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-3">
              <Sparkles className="w-3.5 h-3.5" /> Premium
            </div>
            <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight" data-testid="paywall-title">
              {lang === "en" ? titleEn : titleMs}
            </h1>
            <p className="text-white/70 mt-2" data-testid="paywall-desc">
              {lang === "en" ? descEn : descMs}
            </p>
            <div className="mt-6 flex items-baseline gap-2">
              <div className="text-4xl font-semibold" data-testid="paywall-price">{priceLabel}</div>
              <div className="text-white/70 text-sm">{lang === "en" ? "one-time" : "satu kali"}</div>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="bg-white rounded-3xl p-5 shadow-[0_4px_24px_rgba(0,0,0,0.04)] border border-[#E8E8ED]">
          <div className="text-xs uppercase tracking-[0.15em] text-gray-400 mb-3">
            {lang === "en" ? "You'll unlock" : "Anda akan dapat"}
          </div>
          <ul className="space-y-2.5">
            {FEATURES.map((f, i) => (
              <li key={i} className="flex items-start gap-3 text-sm text-[#1D1D1F]">
                <span className="mt-0.5 w-5 h-5 rounded-full bg-[#15C3C6]/15 flex items-center justify-center flex-shrink-0">
                  <Check className="w-3 h-3 text-[#15C3C6]" strokeWidth={3} />
                </span>
                {f}
              </li>
            ))}
          </ul>
        </section>

        {/* Platform-specific buttons */}
        {isIOS && (
          <div className="space-y-2" data-testid="paywall-apple-block">
            <button
              data-testid="buy-apple-btn"
              onClick={buyApple}
              disabled={busy}
              className="w-full py-4 rounded-full bg-[#D4AF37] hover:bg-[#c39d2b] text-[#0A2540] font-semibold shadow-[0_12px_32px_rgba(212,175,55,0.35)] transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
            >
              <Apple className="w-4 h-4" />
              {busy ? (lang === "en" ? "Processing…" : "Sedang memproses…") : (lang === "en" ? "Buy with Apple Pay" : "Beli dengan Apple Pay")}
            </button>
            <button
              data-testid="restore-btn"
              onClick={restoreApple}
              disabled={busy}
              className="w-full py-3 rounded-full bg-white border border-[#E8E8ED] text-[#0A2540] font-medium hover:bg-[#F5F5F7] transition-colors flex items-center justify-center gap-2"
            >
              <RefreshCw className="w-4 h-4" /> {lang === "en" ? "Restore Purchases" : "Pulihkan Pembelian"}
            </button>
            <p className="text-[11px] text-gray-500 text-center leading-relaxed">
              {lang === "en"
                ? "Payment will be charged to your Apple ID. One-time; no auto-renewal."
                : "Pembayaran akan dicaj kepada Apple ID anda. Bayaran satu kali; tiada pembaharuan automatik."}
            </p>
          </div>
        )}

        {isWebFlow && (
          <div className="space-y-3" data-testid="paywall-stripe-block">
            {/* Currency selector */}
            <div className="inline-flex items-center gap-2 bg-white border border-[#E8E8ED] rounded-full p-1 mx-auto">
              <span className="text-[11px] text-gray-400 pl-3 pr-1">{lang === "en" ? "Currency" : "Mata Wang"}:</span>
              <button
                data-testid="currency-myr"
                onClick={() => setCurrency("myr")}
                className={`px-3 py-1 text-xs rounded-full font-medium transition-colors ${currency === "myr" ? "bg-[#0A2540] text-white" : "text-gray-500"}`}
              >MYR</button>
              <button
                data-testid="currency-usd"
                onClick={() => setCurrency("usd")}
                className={`px-3 py-1 text-xs rounded-full font-medium transition-colors ${currency === "usd" ? "bg-[#0A2540] text-white" : "text-gray-500"}`}
              >USD</button>
            </div>
            <button
              data-testid="buy-stripe-btn"
              onClick={buyStripe}
              disabled={busy}
              className="w-full py-4 rounded-full bg-[#635BFF] hover:bg-[#5348ee] text-white font-semibold shadow-[0_12px_32px_rgba(99,91,255,0.35)] transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
            >
              <Lock className="w-4 h-4" />
              {busy ? (lang === "en" ? "Redirecting…" : "Mengalih ke Stripe…") : (lang === "en" ? "Pay Once with Stripe" : "Bayar Sekali dengan Stripe")}
            </button>
            <div className="flex items-center justify-center gap-2 text-[11px] text-gray-500 pt-1">
              <ShieldCheck className="w-3 h-3" />
              {lang === "en" ? "Secure Stripe Checkout" : "Stripe Checkout selamat"}
            </div>
          </div>
        )}

        {!isIOS && !isWebFlow && (
          <div className="text-center text-sm text-gray-500 py-6">
            {lang === "en" ? "Payments are not available on this platform." : "Pembayaran tidak tersedia pada platform ini."}
          </div>
        )}

        <p className="text-[11px] text-gray-500 text-center leading-relaxed">
          {lang === "en" ? "By continuing you agree to our" : "Dengan meneruskan anda bersetuju dengan"}{" "}
          <Link to="/terms" className="underline">{lang === "en" ? "Terms of Use" : "Terma Penggunaan"}</Link>{" "}
          {lang === "en" ? "and" : "dan"}{" "}
          <Link to="/privacy" className="underline">{lang === "en" ? "Privacy Policy" : "Dasar Privasi"}</Link>.
        </p>
      </main>
    </div>
  );
}
